import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignmentsApi } from '@/lib/api-client';
import { notifications } from '@mantine/notifications';

export function useMyAssignments(includeCompleted: boolean = false, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['assignments', includeCompleted],
    queryFn: () => assignmentsApi.getAssignments(includeCompleted),
    enabled: options?.enabled ?? true,
  });
}

export function useAssignment(assignmentId: string) {
  return useQuery({
    queryKey: ['assignment', assignmentId],
    queryFn: () => assignmentsApi.getAssignment(assignmentId),
    enabled: !!assignmentId,
  });
}

export function useCompleteAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, sessionId }: { assignmentId: string; sessionId: string }) =>
      assignmentsApi.completeAssignment(assignmentId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      notifications.show({
        title: 'Success!',
        message: 'Assignment completed successfully',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to complete assignment',
        color: 'red',
      });
    },
  });
}

export function usePendingAssignmentsCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['assignments', 'count'],
    queryFn: () => assignmentsApi.getPendingAssignmentsCount(),
    enabled: options?.enabled ?? true,
  });
}
